import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';
import { useRegion } from '../hooks/useRegion';
import { buildSeoMeta, SITE_NAME, type PageMeta } from './seoConfig';

/**
 * Per-page SEO + Open Graph + Twitter meta for Conscierra (multi-region).
 *
 * Renders into <head> via vite-react-ssg's <Head> (react-helmet-async), so the
 * tags are captured during static prerender — crawlers and social scrapers read
 * the correct per-page title/description/canonical/OG without executing JS.
 * Replaces the client-only usePageMeta hook (which never ran during prerender).
 *
 * The canonical origin is region-aware: useRegion() resolves the active
 * RegionConfig (build-time VITE_HOST during per-region SSG; window.location at
 * runtime), so dist/sg/about.html canonicalises to https://conscierra.sg/about.
 * canonicalPath defaults to the current route. Go-live readiness P0 #6.
 */
export function Seo({ title, description, canonicalPath, ogImage, noindex }: PageMeta) {
  const region = useRegion();
  const { pathname } = useLocation();
  const meta = buildSeoMeta(region.host, {
    title,
    description,
    canonicalPath: canonicalPath ?? pathname,
    ogImage,
    noindex,
  });

  return (
    <Head>
      <title>{meta.fullTitle}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots} />
      <link rel="canonical" href={meta.canonicalUrl} />

      <meta property="og:title" content={meta.fullTitle} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={meta.canonicalUrl} />
      <meta property="og:image" content={meta.imageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.fullTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.imageUrl} />
    </Head>
  );
}

export default Seo;
